// Asistente a pantalla completa dentro del shell «Orion»: historial + chat real
// con el agente de la org (mismo ChatPane y contrato que /chat).
//   ?nuevo=<n>  → abre una conversación nueva (botón «Nuevo chat»)
//   ?hilo=<id>  → reabre una conversación del historial

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, Minimize2 } from 'lucide-react';

import { ChatPane } from '@/components/assistant/ChatPane';
import { ThreadHeaderTitle } from '@/components/assistant/ThreadTitle';
import { requestDockOpen, type DockReturnState } from '@/components/assistant/activeThread';
import { renameThread } from '@/lib/api';
import { shellBrand } from '../brand';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { useMyThreads } from '../data';
import { HOME_SUGGESTIONS } from './OrionHome';
import { Button } from '../ui';

export function OrionAssistantPage({ webChatHeaders }: { webChatHeaders: Record<string, string> }) {
  const brand = shellBrand(useDashboardShell().config);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const nuevo = sp.get('nuevo');
  const hilo = sp.get('hilo');
  const threads = useMyThreads();
  const qc = useQueryClient();
  // Hilo visible: el del historial (?hilo) o el que acaba de iniciar el chat.
  const [startedId, setStartedId] = useState<string | null>(null);
  const [titleOverride, setTitleOverride] = useState<Record<string, string | null>>({});
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  // El historial vive en la barra lateral del shell (pestaña «Chats», SidebarChats):
  // aquí no se duplica (Samuel, 24-oct: «me gusta más el integrado en el panel de la izquierda»).
  const newChat = () => {
    setStartedId(null);
    setSp({ nuevo: String(Date.now()) });
  };

  const shownId = hilo ?? startedId;
  // «Minimizar»: vuelve al orbe flotante CON esta conversación (activeThread.ts),
  // a la pantalla desde la que se amplió (o al inicio).
  const minimize = () => {
    requestDockOpen(shownId);
    navigate((location.state as DockReturnState | null)?.fromDock || '/');
  };
  const listed = threads.data?.find((t) => t.id === shownId);
  const shownTitle = (shownId && titleOverride[shownId]) || (listed?.hasTitle ? listed.title : null) || null;

  // El backend nombra el hilo en segundo plano tras el 1er intercambio.
  const onTitlePending = useCallback(() => {
    [2500, 7000].forEach((ms) => timers.current.push(window.setTimeout(() => void threads.refetch(), ms)));
  }, [threads]);
  const onThreadInfo = useCallback((id: string, info: { title: string | null }) => {
    setTitleOverride((m) => (m[id] === info.title ? m : { ...m, [id]: info.title }));
  }, []);
  const rename = async (id: string, title: string) => {
    qc.setQueryData(['orion-shell', 'threads'], (old: typeof threads.data) =>
      old?.map((t) => (t.id === id ? { ...t, title, hasTitle: true } : t)),
    );
    setTitleOverride((m) => ({ ...m, [id]: title }));
    try {
      await renameThread(id, title);
      window.dispatchEvent(new CustomEvent('orion-thread-renamed', { detail: { id, title } }));
    } catch {
      setTitleOverride((m) => ({ ...m, [id]: null }));
    }
    void threads.refetch();
  };

  return (
    <div className="flex h-[calc(100dvh-4rem)] min-h-[480px]">
      <section aria-label="Chat" className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <ThreadHeaderTitle
            as="h1"
            title={shownTitle}
            fallback={`Asistente de ${brand.assistantName}`}
            className="flex-1 text-base font-semibold text-foreground"
            onRename={shownId ? (t) => rename(shownId, t) : undefined}
          />
          <Button variant="secondary" onClick={newChat} className="md:hidden" aria-label="Nuevo chat">
            <MessageSquarePlus size={15} aria-hidden />
          </Button>
          <Button variant="secondary" onClick={minimize} aria-label="Minimizar al chat flotante (la conversación sigue ahí)">
            <Minimize2 size={15} aria-hidden />
            <span className="hidden sm:inline">Minimizar</span>
          </Button>
        </div>
        <div className="min-h-0 flex-1">
          <ChatPane
            key={hilo ? `h-${hilo}` : `n-${nuevo ?? 'base'}`}
            threadId={hilo}
            startFresh={!hilo && !!nuevo}
            webChatHeaders={webChatHeaders}
            hideThinking
            assistantAvatarUrl={brand.symbol}
            suggestions={brand.suggestions ?? HOME_SUGGESTIONS}
            onThreadStarted={(id) => {
              setStartedId(id);
              void threads.refetch();
            }}
            modelPicker
            mentions
            shareThread
            onThreadInfo={onThreadInfo}
            onTitlePending={onTitlePending}
            emptyTitle="¿En qué te ayudo hoy?"
            emptySubtitle={brand.subtitle}
          />
        </div>
      </section>
    </div>
  );
}

export default OrionAssistantPage;
